/**
 * Toast Service
 * 
 * High-level service for displaying toasts with automatic deduplication.
 * Follows the Facade Pattern to provide a simple API while hiding complexity.
 * 
 * Architecture:
 * - Facade pattern: Simple API, complex implementation hidden
 * - Dependency injection: ToastManager injected for testability
 * - Single responsibility: Only handles toast display logic
 */

import toast from 'react-hot-toast';
import { toastManager } from './ToastManager';
import { 
  TOAST_STYLES, 
  TOAST_ICONS, 
  TOAST_DURATIONS,
  DEFAULT_ICON_THEME 
} from '../../constants/toastConstants';

class ToastService {
  /**
   * Create a ToastService instance
   * @param {ToastManager} manager - ToastManager instance (defaults to singleton)
   * @param {Object} options - Configuration options
   * @param {number} options.maxFallbackTimeout - Maximum fallback timeout in ms (default: 5 minutes)
   */
  constructor(manager = toastManager, options = {}) {
    this.manager = manager;
    this.fallbackTimeouts = new Map();
    this.MAX_FALLBACK_TIMEOUT = options.maxFallbackTimeout || 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Show a toast with deduplication
   * @param {Function} toastMethod - The toast method (toast.error, toast.success, etc.)
   * @param {string} message - The message to display
   * @param {object} options - Toast options
   * @returns {string|null} Toast ID if shown, null if duplicate
   */
  show(toastMethod, message, options = {}) {
    // Input validation
    if (!message || typeof message !== 'string') {
      console.warn('ToastService: Invalid message provided');
      return null;
    }

    if (typeof toastMethod !== 'function') {
      console.warn('ToastService: Invalid toast method provided');
      return null;
    }

    try {
      if (!this.manager.tryRegisterMessage(message)) {
        return null;
      }
    } catch (error) {
      console.error('ToastService: Error registering message:', error);
      return null;
    }

    const toastDuration = options.duration || TOAST_DURATIONS.STANDARD;
    
    let isCleanedUp = false;
    let fallbackTimeoutId = null;
    let toastIdRef = null;
    
    const cleanup = (source = 'onDismiss') => {
      if (isCleanedUp) return;
      
      if (fallbackTimeoutId !== null) {
        clearTimeout(fallbackTimeoutId);
        if (toastIdRef) {
          this.fallbackTimeouts.delete(toastIdRef);
        }
        fallbackTimeoutId = null;
        
        if (source === 'fallback') {
          console.warn(
            `ToastService: Fallback cleanup triggered for toast "${message.substring(0, 50)}...". ` +
            `onDismiss callback did not fire within expected time.`
          );
        }
      }
      
      this.manager.unregisterMessage(message);
      isCleanedUp = true;
    };
    
    const enhancedOptions = {
      ...options,
      duration: toastDuration,
      onDismiss: (toastId) => {
        cleanup('onDismiss');
        if (options.onDismiss && typeof options.onDismiss === 'function') {
          options.onDismiss(toastId);
        }
      }
    };

    let toastId;
    try {
      toastId = toastMethod(message, enhancedOptions);
    } catch (error) {
      this.manager.unregisterMessage(message);
      console.error('ToastService: Error creating toast:', error, {
        message: message.substring(0, 50),
        toastMethod: toastMethod.name || 'unknown'
      });
      return null;
    }
    
    if (!toastId) {
      this.manager.unregisterMessage(message);
      return null;
    }
    
    toastIdRef = toastId;
    
    // react-hot-toast has no onDismiss callback, so the "onDismiss" option above
    // never fires - this fallback timeout is the only cleanup path. It must track
    // the toast's actual visible duration (plus a small buffer for the dismiss
    // animation), not a multiple of it, or a toast can still be "active" for
    // deduplication purposes well after it has disappeared from the screen -
    // silently suppressing a legitimate next toast with the same message.
    const fallbackTimeout = (toastDuration === Infinity || !isFinite(toastDuration))
      ? this.MAX_FALLBACK_TIMEOUT
      : Math.min(toastDuration + 300, this.MAX_FALLBACK_TIMEOUT);
    
    fallbackTimeoutId = setTimeout(() => cleanup('fallback'), fallbackTimeout);
    this.fallbackTimeouts.set(toastId, fallbackTimeoutId);
    
    return toastId;
  }

  /**
   * Show error toast
   * Uses toast.error() which relies on Toaster config from App.js
   * @param {string} message - Error message
   * @param {object} options - Toast options
   * @returns {string|null} Toast ID
   */
  error(message, options = {}) {
    return this.show(toast.error, message, {
      duration: options.duration ?? TOAST_STYLES.error?.duration ?? TOAST_DURATIONS.STANDARD,
      // Icon and iconTheme come from Toaster config in App.js
      // Explicitly set icon for consistency with TOAST_ICONS constant
      icon: options.icon ?? TOAST_ICONS.ERROR,
      iconTheme: options.iconTheme ?? TOAST_STYLES.error?.iconTheme ?? DEFAULT_ICON_THEME,
      ...options,
    });
  }

  /**
   * Show success toast
   * Uses toast.success() which relies on Toaster config from App.js
   * @param {string} message - Success message
   * @param {object} options - Toast options
   * @returns {string|null} Toast ID
   */
  success(message, options = {}) {
    return this.show(toast.success, message, {
      duration: options.duration ?? TOAST_STYLES.success?.duration ?? TOAST_DURATIONS.MEDIUM,
      // Icon and iconTheme come from Toaster config in App.js
      // Explicitly set icon for consistency with TOAST_ICONS constant
      icon: options.icon ?? TOAST_ICONS.SUCCESS,
      iconTheme: options.iconTheme ?? TOAST_STYLES.success?.iconTheme ?? DEFAULT_ICON_THEME,
      ...options,
    });
  }

  /**
   * Show info toast
   * Uses base toast() function with explicit style/iconTheme from TOAST_STYLES
   * These values match the Toaster config in App.js, ensuring consistency
   * @param {string} message - Info message
   * @param {object} options - Toast options
   * @returns {string|null} Toast ID
   */
  info(message, options = {}) {
    const infoConfig = TOAST_STYLES.info || {};
    
    // Use base toast() with explicit style/iconTheme from TOAST_STYLES
    // These match the Toaster config in App.js, ensuring Path A consistency
    return this.show(
      (msg, opts) => toast(msg, {
        ...opts,
        icon: opts.icon ?? TOAST_ICONS.INFO,
        style: {
          ...infoConfig.style,
          ...opts.style, // Allow override
        },
        iconTheme: opts.iconTheme ?? infoConfig.iconTheme ?? DEFAULT_ICON_THEME,
      }),
      message,
      {
        duration: options.duration ?? infoConfig.duration ?? TOAST_DURATIONS.STANDARD,
        ...options,
      }
    );
  }

  /**
   * Show warning toast
   * Uses base toast() function with explicit style/iconTheme from TOAST_STYLES
   * These values match the Toaster config in App.js, ensuring consistency
   * @param {string} message - Warning message
   * @param {object} options - Toast options
   * @returns {string|null} Toast ID
   */
  warning(message, options = {}) {
    const warningConfig = TOAST_STYLES.warning || {};
    
    // Use base toast() with explicit style/iconTheme from TOAST_STYLES
    // These match the Toaster config in App.js, ensuring Path A consistency
    return this.show(
      (msg, opts) => toast(msg, {
        ...opts,
        icon: opts.icon ?? TOAST_ICONS.WARNING,
        style: {
          ...warningConfig.style,
          ...opts.style, // Allow override
        },
        iconTheme: opts.iconTheme ?? warningConfig.iconTheme ?? DEFAULT_ICON_THEME,
      }),
      message,
      {
        duration: options.duration ?? warningConfig.duration ?? TOAST_DURATIONS.STANDARD,
        ...options,
      }
    );
  }

  /**
   * Show loading toast with deduplication
   * Loading toasts are persistent (duration: Infinity) and require manual dismissal
   * @param {string} message - Loading message
   * @param {object} options - Toast options (duration will be overridden to Infinity)
   * @returns {string|null} Toast ID if shown, null if duplicate
   */
  loading(message, options = {}) {
    return this.show(toast.loading, message, {
      ...options,
      duration: Infinity,
    });
  }

  /**
   * Cleanup method for service teardown
   * Clears all fallback timeouts
   */
  cleanup() {
    this.fallbackTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.fallbackTimeouts.clear();
  }
}

// Export singleton instance (default)
export const toastService = new ToastService();

// Export class for testing and custom instances
export { ToastService };

/**
 * Create a custom ToastService instance (useful for testing)
 * @param {ToastManager} manager - Custom ToastManager instance
 * @param {Object} options - Configuration options
 * @returns {ToastService} New ToastService instance
 */
export const createToastService = (manager, options) => {
  return new ToastService(manager, options);
};

